import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useNotify, usePermissions, useRecordContext } from "react-admin";

import { callApi } from "@/dataprovider/misc-apis";
import { formatListDate } from "@/utils/date-format";
import { hasPermission } from "@/utils/permission-utils";
import type { PermissionRecord } from "@/types/permissions";
import { usePayrollSetupBanner } from "@/containers/staff/shared/StaffPayrollSetupIncompleteBanner";
import {
  canViewEmployeeFinancials,
  canWriteEmployeeSalaryHistory,
} from "@/containers/staff/shared/staff-payroll-permissions";

import {
  EMPTY_SALARY_HISTORY_FORM,
  EMPLOYEE_SALARY_HISTORY_LOCATION,
  extractSalaryHistoryRows,
  formatMonthlyGrossInr,
  validateSalaryHistoryForm,
  type SalaryHistoryFormValues,
  type SalaryHistoryRow,
} from "../salary-history-form";

function apiErrorMessage(err: unknown, fallback: string): string {
  const body = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data;
  const msg = body?.message;
  if (Array.isArray(msg)) return msg.join(", ");
  if (typeof msg === "string" && msg.trim()) return msg;
  return fallback;
}

/**
 * Staff Edit: list salary history and add a new monthly gross effective-from row.
 * Hidden without payrollPeriods.view — roster `employees.view` alone is not enough.
 * Writes need payroll create/edit and a complete payroll setup.
 */
export function StaffSalaryHistorySection() {
  const record = useRecordContext<{ id: number }>();
  const notify = useNotify();
  const { permissions } = usePermissions<PermissionRecord>();
  const canView = canViewEmployeeFinancials((name) => hasPermission(permissions, name));
  const canWrite = canWriteEmployeeSalaryHistory((name) => hasPermission(permissions, name));
  const { loading: setupLoading, payrollSetupComplete } = usePayrollSetupBanner();
  const writesAllowed = canWrite && !setupLoading && payrollSetupComplete;

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<SalaryHistoryRow[]>([]);
  const [form, setForm] = useState<SalaryHistoryFormValues>(EMPTY_SALARY_HISTORY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const employeeId = record?.id != null ? Number(record.id) : 0;

  const fetchRows = useCallback(async () => {
    if (!canView || !employeeId) return;
    setLoading(true);
    try {
      const { data } = await callApi({
        location: EMPLOYEE_SALARY_HISTORY_LOCATION,
        method: "GET",
        data: {
          employeeId,
          limit: 50,
          orderBy: "effectiveFrom",
          order: "DESC",
        },
      });
      setRows(extractSalaryHistoryRows(data));
    } catch (err) {
      notify(apiErrorMessage(err, "Failed to load salary history"), { type: "error" });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [canView, employeeId, notify]);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  const handleSubmit = async () => {
    const validated = validateSalaryHistoryForm(employeeId, form);
    if (!validated.ok) {
      notify(validated.error, { type: "warning" });
      return;
    }
    if (!canWrite) {
      notify("You do not have permission to change salary history.", { type: "warning" });
      return;
    }
    if (!writesAllowed) {
      notify("Complete payroll setup in Staff settings before adding salary history.", {
        type: "warning",
      });
      return;
    }
    setSubmitting(true);
    try {
      await callApi({
        location: EMPLOYEE_SALARY_HISTORY_LOCATION,
        method: "POST",
        data: validated.payload,
      });
      notify("Salary history added", { type: "success" });
      setForm(EMPTY_SALARY_HISTORY_FORM);
      await fetchRows();
    } catch (err) {
      notify(apiErrorMessage(err, "Failed to add salary history"), { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  if (!record || !canView) return null;

  return (
    <Box sx={{ mt: 2, width: "100%" }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Salary history
      </Typography>

      {!canWrite ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          You can view salary history but cannot change it.
        </Alert>
      ) : !writesAllowed && !setupLoading ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Salary history cannot be updated until payroll setup is complete.{" "}
          <Button
            component={RouterLink}
            to="/staffSettings"
            size="small"
            color="inherit"
            sx={{ verticalAlign: "baseline", textTransform: "none", minWidth: 0, p: 0 }}
          >
            Open Staff settings
          </Button>
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          No salary history yet.
        </Typography>
      ) : (
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Effective from</TableCell>
              <TableCell align="right">Monthly gross</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{formatListDate(row.effectiveFrom, { empty: "—" })}</TableCell>
                <TableCell align="right">{formatMonthlyGrossInr(row.monthlyGross)}</TableCell>
                <TableCell>{row.notes?.trim() || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {canWrite ? (
        <Stack spacing={1.5}>
          <Typography variant="body2" fontWeight={600}>
            Add salary row
          </Typography>
          <TextField
            label="Monthly gross (INR)"
            type="number"
            value={form.monthlyGross}
            onChange={(e) => setForm((prev) => ({ ...prev, monthlyGross: e.target.value }))}
            fullWidth
            size="small"
            disabled={!writesAllowed || submitting}
            inputProps={{ min: 1, step: 1 }}
          />
          <TextField
            label="Effective from"
            type="date"
            value={form.effectiveFrom}
            onChange={(e) => setForm((prev) => ({ ...prev, effectiveFrom: e.target.value }))}
            fullWidth
            size="small"
            disabled={!writesAllowed || submitting}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Notes"
            value={form.notes}
            onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
            fullWidth
            size="small"
            multiline
            minRows={2}
            disabled={!writesAllowed || submitting}
          />
          <Button
            variant="contained"
            onClick={() => void handleSubmit()}
            disabled={!writesAllowed || submitting}
            sx={{ alignSelf: "flex-start" }}
          >
            {submitting ? "Saving…" : "Add salary history"}
          </Button>
        </Stack>
      ) : null}
    </Box>
  );
}
